import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { PageHeader } from "@/app/PageHeader";
import { useAsync } from "@/lib/useAsync";
import { IconCpu } from "@/lib/icons";

import { listDiscoveredDevices } from "../api";
import { DeviceList } from "../components/DeviceList";
import { EmptyState } from "../components/EmptyState";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";

/**
 * Lista técnica de devices descobertos via worker MQTT. Mantida como tela
 * de diagnóstico — acessada via sidebar "Sistema → Diagnóstico técnico".
 */
export function DevicesPage() {
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get("workspaceId") ?? "workspace-local";
  const machineId = searchParams.get("machineId") ?? "main";
  const detailsSearch = `?workspaceId=${encodeURIComponent(workspaceId)}&machineId=${encodeURIComponent(machineId)}`;
  const { state, reload } = useAsync("devices/discovered", (signal) =>
    listDiscoveredDevices(signal),
  );
  const [filter, setFilter] = useState("");

  const filtered = useMemo(() => {
    if (!state.data) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return state.data;
    return state.data.filter((device) => {
      const hay = `${device.mqttId ?? ""} ${device.mac ?? ""} ${device.status ?? ""} ${
        device.serialNumber ?? ""
      } ${device.currentWorkspaceId ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [state.data, filter]);

  const total = state.data?.length ?? 0;

  return (
    <>
      <PageHeader
        crumbs={[
          { label: "Dashboard", to: "/workflows" },
          { label: "Diagnóstico" },
        ]}
        title="Devices descobertos"
        meta={
          <>
            <span className="item">
              <IconCpu size={12} /> Total <b>{total}</b>
            </span>
            {searchParams.has("machineId") ? (
              <span className="item">
                Destino <b>{machineId}</b>
              </span>
            ) : null}
          </>
        }
      />
      <div className="content">
        <div className="section-h">
          <h2>
            Hardwares vistos pelo worker MQTT <span className="num">{total}</span>
          </h2>
          <div className="right">
            <input
              type="search"
              placeholder="Filtrar por mqttId, MAC, status..."
              className="input"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              aria-label="Filtrar devices"
              style={{ minWidth: 260 }}
            />
          </div>
        </div>

        {state.status === "loading" && !state.data ? (
          <LoadingState label="Buscando devices..." />
        ) : null}

        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={reload} />
        ) : null}

        {state.data && filtered.length === 0 ? (
          <EmptyState
            title={filter ? "Nenhum device com esse filtro" : "Nenhum device descoberto ainda"}
            message={
              filter
                ? "Tente outro termo ou limpe o filtro."
                : "Aguardando pacotes MQTT. Confirme que o ESP está ligado e que o worker de ingestão está rodando."
            }
          />
        ) : null}

        {filtered.length > 0 ? <DeviceList devices={filtered} detailsSearch={detailsSearch} /> : null}
      </div>
    </>
  );
}
